import { handleError } from '../../../utils/apiUtils';
import { createLinkStep } from '../api/createLinkStep';
import { createTextStep } from '../api/createTextStep';
import { createUploadStep } from '../api/createUploadStep';
import { createContentStep } from '../api/createContentStep';
import { createThirdPartyVideoStep } from '../api/createThirdPartyVideoStep';
import { editStepNameAndDescription } from '../api/editStepNameAndDescription';
import { editThirdPartyVideoUrl } from '../api/editThirdPartyVideoUrl';
import { editLinkUrl } from '../api/editLinkUrl';
import { CourseStep } from '../models/course';
import { Content } from '../models/content';
import * as Yup from 'yup';
import { StepType } from '../models/stepType';
import trainingService from '../../../services/trainingService';
import StepIcons from '../../../components/StepIcons';

type StepValues = {
  stepType: StepType;
  name: string;
  description: string;
  url?: string;
  contentId?: string;
  selectedContents: Content[];
};

type CreateStepParams = {
  courseId: string;
  stepOrder: number;
  userId: string;
  values: StepValues;
};

type EditStepParams = {
  courseId: string;
  stepOrder: number;
  userId: string;
  values: StepValues;
  existingStep?: CourseStep;
};

type StepTypeOption = {
  value: StepType;
  label: string;
  icon: string;
};

export const mapApiTypeToStepType = (apiType: string, thirdPartySite?: string): StepType | '' => {
  switch (apiType) {
    case 'Text':
      return 'text';
    case 'Upload':
      return 'upload';
    case 'Link':
    case 'MyWSBLink':
      return 'link';
    case 'Assessment':
      return 'assessment';
    case 'Download':
      return 'download';
    case 'Video':
    case 'Audio':
      return 'video';
    case 'ThirdPartyVideo':
      if (thirdPartySite?.toLowerCase() === 'youtube') return 'youtube';
      if (thirdPartySite?.toLowerCase() === 'vimeo') return 'vimeo';
      return '';
    case 'DocumentViewer':
      return 'documentViewer';
    default:
      return '';
  }
};

export const stepTypeOptions: StepTypeOption[] = [
  { value: 'text', label: 'Text', icon: StepIcons.Text },
  { value: 'download', label: 'Download', icon: StepIcons.Download },
  { value: 'assessment', label: 'Assessment', icon: StepIcons.Assessment },
  { value: 'documentViewer', label: 'Document Viewer', icon: StepIcons.DocumentViewer },
  { value: 'video', label: 'Video', icon: StepIcons.Video },
  { value: 'youtube', label: 'YouTube', icon: StepIcons.YouTube },
  { value: 'vimeo', label: 'Vimeo', icon: StepIcons.Vimeo },
  { value: 'link', label: 'Link', icon: StepIcons.Link },
  { value: 'upload', label: 'Upload', icon: StepIcons.Upload },
  { value: 'audio', label: 'Audio', icon: StepIcons.Audio },
];

export const getValidationSchema = (selectedStepType: StepType) => {
  const baseSchema = {
    stepType: Yup.string().required('Step type is required'),
    name: Yup.string().trim().required('Name is required'),
    description: Yup.string().optional(),
  };

  if (selectedStepType === 'link' || selectedStepType === 'youtube' || selectedStepType === 'vimeo') {
    return Yup.object({
      ...baseSchema,
      url: Yup.string().trim().url('Please enter a valid URL').required('URL is required'),
    });
  }

  if (
    selectedStepType === 'video' ||
    selectedStepType === 'download' ||
    selectedStepType === 'assessment' ||
    selectedStepType === 'audio' ||
    selectedStepType === 'documentViewer'
  ) {
    return Yup.object({
      stepType: Yup.string().required('Step type is required'),
      contentId: Yup.string().required('Please select a content'),
    });
  }

  return Yup.object(baseSchema);
};

export const createStep = ({
  courseId,
  stepOrder,
  userId,
  values,
}: CreateStepParams): Promise<CourseStep | CourseStep[]> => {
  if (!userId) {
    return Promise.reject(new Error('User ID is required'));
  }
  switch (values.stepType) {
    case 'text':
      return createTextStep(courseId, values.name, values.description, stepOrder, userId);
    case 'upload':
      return createUploadStep(courseId, values.name, values.description, stepOrder, userId);
    case 'link':
      return createLinkStep(
        courseId,
        values.name,
        values.description,
        stepOrder,
        values.url || '',
        values.stepType as 'link' | 'myWSBLink',
        userId
      );
    case 'youtube':
    case 'vimeo':
      return createThirdPartyVideoStep(
        courseId,
        values.name,
        values.description,
        stepOrder,
        values.url || '',
        values.stepType as 'YouTube' | 'Vimeo',
        userId
      );
    case 'assessment':
    case 'download':
    case 'video':
    case 'audio':
    case 'documentViewer':
      return createContentStep(
        courseId,
        values.selectedContents,
        stepOrder,
        values.stepType as 'assessment' | 'download' | 'video' | 'audio',
        userId
      );
    default:
      return Promise.reject(new Error('Invalid step type'));
  }
};

export const editStep = ({
  courseId,
  stepOrder,
  userId,
  values,
  existingStep,
}: EditStepParams): Promise<CourseStep | CourseStep[]> => {
  if (!userId) {
    return Promise.reject(new Error('User ID is required'));
  }
  switch (values.stepType) {
    case 'youtube':
    case 'vimeo':
      return editThirdPartyVideoUrl(
        courseId,
        existingStep?._id.id || '',
        values.url || '',
        values.stepType as 'YouTube' | 'Vimeo',
        values.name,
        stepOrder,
        userId
      ).then((_: CourseStep) =>
        editStepNameAndDescription(courseId, existingStep, values.name, values.description, stepOrder, userId)
      );
    case 'link':
      return editLinkUrl(courseId, existingStep?._id.id || '', values.url || '', values.name, stepOrder, userId).then(
        (_: CourseStep) =>
          editStepNameAndDescription(courseId, existingStep, values.name, values.description, stepOrder, userId)
      );
    default:
      return editStepNameAndDescription(courseId, existingStep, values.name, values.description, stepOrder, userId);
  }
};

export const handleContentSwitchSubmit = (
  courseId: string,
  stepOrder: number,
  userId: string,
  values: StepValues,
  existingStep: CourseStep,
  onSuccess: (step: CourseStep) => void | Promise<void>,
  handleDismiss: () => void
) => {
  if (!userId) {
    return Promise.resolve();
  }

  return trainingService
    .removeTask(courseId, existingStep._id.id, userId)
    .then(() => {
      return createStep({
        courseId,
        stepOrder,
        userId,
        values,
      }).then((steps: CourseStep | CourseStep[]) => {
        if (Array.isArray(steps)) {
          steps.forEach(step => onSuccess({ ...step, order: stepOrder }));
        } else {
          onSuccess(steps);
        }
        handleDismiss();
      });
    })
    .catch(handleError);
};
